<!-- src/views/training/TrainingUploadView.vue -->
<template>
  <v-container class="py-6" max-width="800">
    <!-- Header -->
    <div class="d-flex align-center mb-6">
      <v-icon color="info" size="32" class="mr-3">mdi-video-plus-outline</v-icon>
      <div>
        <h1 class="text-h5 font-weight-bold">Upload video for training</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Upload a backgammon video for dice training</p>
      </div>
    </div>

    <!-- Upload Card -->
    <v-card variant="outlined" class="mb-6">
      <v-card-text class="pa-6">
        <!-- Drop zone -->
        <div
          class="upload-zone"
          :class="{ 'drag-over': isDragging, 'has-file': selectedFile }"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="onDrop"
          @click="triggerFileInput"
        >
          <input
            ref="fileInput"
            type="file"
            accept="video/mp4,video/avi,video/mov,video/mkv,video/webm,.mp4,.avi,.mov,.mkv,.webm"
            style="display: none"
            @change="onFileSelected"
          />

          <template v-if="!selectedFile">
            <v-icon size="48" color="grey">mdi-cloud-upload-outline</v-icon>
            <div class="text-body-1 mt-2">Drag a video here or click to select</div>
            <div class="text-caption text-medium-emphasis mt-1">MP4, AVI, MOV, MKV, WebM</div>
          </template>

          <template v-else>
            <v-icon size="48" color="info">mdi-video-check-outline</v-icon>
            <div class="text-body-1 mt-2 font-weight-medium">{{ selectedFile.name }}</div>
            <div class="text-caption text-medium-emphasis mt-1">
              {{ formatFileSize(selectedFile.size) }}
            </div>
            <v-btn size="small" variant="text" color="error" class="mt-2" @click.stop="clearFile">
              <v-icon start size="small">mdi-close</v-icon>
              Fjern
            </v-btn>
          </template>
        </div>

        <!-- FPS setting -->
        <v-text-field
          v-model.number="fps"
          label="Frames per sekund (FPS)"
          type="number"
          min="0.1"
          max="30"
          step="0.5"
          hint="How many images should be extracted per second of video. 1 = one image per second."
          persistent-hint
          class="mt-4"
          style="max-width: 300px"
        />

        <!-- Upload progress -->
        <v-progress-linear
          v-if="uploading"
          :model-value="uploadProgress"
          color="info"
          height="8"
          rounded
          class="mt-4"
        />

        <v-btn
          color="primary"
          size="large"
          block
          class="mt-4"
          :loading="uploading"
          :disabled="!selectedFile"
          @click="uploadVideo"
        >
          <v-icon start>mdi-upload</v-icon>
          Upload video
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Files on server (FTP) -->
    <v-card variant="outlined" class="mb-6" v-if="pendingFiles.length > 0">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" size="20">mdi-server</v-icon>
        Files on server (FTP)
        <v-spacer />
        <v-btn
          size="small"
          color="primary"
          variant="tonal"
          :loading="registeringAll"
          :disabled="registeringFile !== null"
          @click="registerAll"
        >
          <v-icon start size="small">mdi-playlist-plus</v-icon>
          Register all ({{ pendingFiles.length }})
        </v-btn>
      </v-card-title>
      <v-card-subtitle class="pb-2">
        Videos found in the server folder that are not yet registered. They use the FPS set above.
      </v-card-subtitle>
      <v-divider />
      <v-list density="compact">
        <v-list-item v-for="f in pendingFiles" :key="f">
          <template #prepend>
            <v-icon color="grey">mdi-video-outline</v-icon>
          </template>
          <v-list-item-title>{{ f }}</v-list-item-title>
          <template #append>
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              :loading="registeringFile === f"
              :disabled="registeringAll || (registeringFile !== null && registeringFile !== f)"
              @click="registerFile(f)"
            >
              Register
            </v-btn>
          </template>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Recent uploads -->
    <v-card variant="outlined" v-if="recentUploads.length > 0">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" size="20">mdi-history</v-icon>
        Recent uploads
      </v-card-title>
      <v-divider />
      <v-list density="compact">
        <v-list-item v-for="u in recentUploads" :key="u.videoId">
          <template #prepend>
            <v-icon :color="statusColor(u.status)">mdi-video-outline</v-icon>
          </template>
          <v-list-item-title>{{ u.originalName }}</v-list-item-title>
          <v-list-item-subtitle>
            {{ formatDate(u.createdAt) }}
          </v-list-item-subtitle>
          <template #append>
            <v-chip size="small" :color="statusColor(u.status)" variant="tonal">
              {{ statusLabel(u.status) }}
            </v-chip>
          </template>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import api from '../../services/api';

const fileInput = ref(null);
const selectedFile = ref(null);
const fps = ref(1.0);
const isDragging = ref(false);
const uploading = ref(false);
const uploadProgress = ref(0);
const recentUploads = ref([]);
const pendingFiles = ref([]);
const registeringFile = ref(null);
const registeringAll = ref(false);

const snackbar = ref(false);
const snackbarText = ref('');
const snackbarColor = ref('success');

function showSnack(text, color = 'success') {
  snackbarText.value = text;
  snackbarColor.value = color;
  snackbar.value = true;
}

function formatFileSize(bytes) {
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(dateStr) {
  if (!dateStr) return '–';
  return new Date(dateStr).toLocaleDateString('da-DK', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function statusColor(status) {
  switch (status) {
    case 'pending': return 'grey';
    case 'processing': return 'blue';
    case 'extracted': return 'info';
    case 'completed': return 'success';
    case 'error': return 'error';
    default: return 'grey';
  }
}

function statusLabel(status) {
  switch (status) {
    case 'pending': return 'Pending';
    case 'processing': return 'Processing...';
    case 'extracted': return 'Frames ready';
    case 'completed': return 'Completed';
    case 'error': return 'Error';
    default: return status;
  }
}

function triggerFileInput() {
  fileInput.value?.click();
}

function onFileSelected(e) {
  const file = e.target.files?.[0];
  if (file) selectedFile.value = file;
}

function onDrop(e) {
  isDragging.value = false;
  const file = e.dataTransfer?.files?.[0];
  if (file && /\.(mp4|avi|mov|mkv|webm)$/i.test(file.name)) {
    selectedFile.value = file;
  } else {
    showSnack('Only video files are allowed (MP4, AVI, MOV, MKV, WebM)', 'warning');
  }
}

function clearFile() {
  selectedFile.value = null;
  if (fileInput.value) fileInput.value.value = '';
}

async function uploadVideo() {
  if (!selectedFile.value) return;

  uploading.value = true;
  uploadProgress.value = 0;

  try {
    const formData = new FormData();
    formData.append('video', selectedFile.value);
    formData.append('fps', fps.value.toString());

    const { data } = await api.post('/training/videos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        uploadProgress.value = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
      },
    });

    showSnack(`Video uploadet: ${data.originalName} (ID: ${data.videoId})`);
    clearFile();
    await loadRecentUploads();
  } catch (err) {
    showSnack('Upload fejlede: ' + (err.response?.data?.error || err.message), 'error');
  }
  uploading.value = false;
}

async function loadRecentUploads() {
  try {
    const { data } = await api.get('/training/videos');
    recentUploads.value = (data.videos || []).slice(0, 5);
    pendingFiles.value = data.pendingFiles || [];
  } catch {}
}

async function registerFile(filename) {
  registeringFile.value = filename;
  try {
    const { data } = await api.post('/training/videos', {
      filename,
      fps: fps.value || 1.0,
    });
    showSnack(`Registered: ${data.originalName} (ID: ${data.videoId})`);
    await loadRecentUploads();
  } catch (err) {
    showSnack('Register failed: ' + (err.response?.data?.error || err.message), 'error');
  }
  registeringFile.value = null;
}

async function registerAll() {
  registeringAll.value = true;
  let ok = 0;
  let failed = 0;
  for (const filename of [...pendingFiles.value]) {
    try {
      await api.post('/training/videos', {
        filename,
        fps: fps.value || 1.0,
      });
      ok++;
    } catch {
      failed++;
    }
  }
  await loadRecentUploads();
  showSnack(
    failed ? `Registered ${ok}, failed ${failed}` : `Registered ${ok} videos`,
    failed ? 'warning' : 'success'
  );
  registeringAll.value = false;
}

onMounted(() => {
  loadRecentUploads();
});
</script>

<style scoped>
.upload-zone {
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}
.upload-zone:hover {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.02);
}
.upload-zone.drag-over {
  border-color: rgb(var(--v-theme-info));
  background: rgba(var(--v-theme-info), 0.05);
}
.upload-zone.has-file {
  border-color: rgb(var(--v-theme-info));
  border-style: solid;
}
</style>